// Comprehensive Colorado family law data based on FindLaw and official Colorado sources
const coloradoData = {
  'colorado': {
    name: 'Colorado',
    overview: {
      keyPoints: [
        'Colorado uses the term "Allocation of Parental Responsibilities" (APR) instead of "custody"',
        'The state divides responsibilities into "Parenting Time" (physical custody) and "Decision-Making Responsibility" (legal custody)',
        'Courts determine allocation based on the "best interests of the child" standard outlined in C.R.S. § 14-10-124',
        'Colorado does not presume equal parenting time, but encourages frequent and continuing contact with both parents',
        'Parenting plans are mandatory in all cases involving children',
        'Mandatory parenting classes are required in most judicial districts for divorcing parents',
        'Relocation with a child requires specific court approval if it substantially changes the geographical ties',
        'Grandparents regarding visitation rights have specific standing requirements to petition the court'
      ],
      uniqueFeatures: [
        'Colorado completely replaced "custody" and "visitation" terminology with "parental responsibilities" and "parenting time"',
        'The state uses a specific "Child Support Worksheet" that accounts for the number of overnights',
        'Colorado allows for the appointment of a Child and Family Investigator (CFI) or Parental Responsibilities Evaluator (PRE) in contested cases',
        'The state has a "182-day rule" (approx. 6 months) for establishing home state jurisdiction',
        'Colorado courts often require mediation before a contested hearing can be scheduled'
      ]
    },
    legalRequirements: {
      residency: 'The child must have lived in Colorado for at least 182 days (6 months) before filing, or since birth if under 6 months',
      filing: 'File a Petition for Allocation of Parental Responsibilities in the District Court of the county where the child resides',
      paternity: 'Paternity can be established through voluntary acknowledgment, genetic testing, or administrative order',
      documentation: 'Required documents include Petition, Summons, Case Information Sheet, and Parenting Plan',
      uniqueRules: [
        'Mandatory parenting class required within 45 days of filing',
        'Mandatory financial disclosures (Sworn Financial Statement) required in all cases',
        'Mediation is usually ordered by the court if parents cannot agree on a parenting plan',
        'Initial status conferences are held early in the case to manage the process'
      ]
    },
    custodyTypes: {
      physicalCustody: 'Referred to as "Parenting Time" - the schedule of when the child is in each parent\'s care (counted by overnights)',
      legalCustody: 'Referred to as "Decision-Making Responsibility" - authority to make major decisions (education, health, religion, extracurriculars)',
      jointCustody: 'Joint Decision-Making is common; parenting time is allocated based on best interests, not necessarily 50/50',
      visitation: 'Referred to as "Parenting Time"; terms like "visitation" are obsolete in Colorado statutes',
      specialProvisions: [
        'Supervised parenting time may be ordered if a parent poses a danger to the child',
        'Decision-making can be split (e.g., one parent decides education, the other decides health) or joint',
        'A "tie-breaker" (like an arbitrator) can be appointed for joint decision-making disputes'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Specific schedule for regular parenting time (weekdays/weekends)',
        'Holiday and vacation schedule (alternating years/split holidays)',
        'Allocation of decision-making responsibility (sole or joint)',
        'Child support calculation and payment method',
        'Tax exemption allocation for the children',
        'Exchange location and transportation logistics',
        'Communication rules between parents',
        'Dispute resolution process (e.g., mediation) for future disagreements'
      ],
      tips: [
        'Be precise about exchange times and locations to avoid conflict',
        'Account for Colorado\'s weather when planning transportation for mountain/rural exchanges',
        'Specify how "Right of First Refusal" for childcare will work',
        'Include provisions for travel out of state or country',
        'Address electronic communication access (FaceTime/Zoom) with the other parent'
      ]
    },
    childSupport: {
      guidelines: 'Colorado uses the "Income Shares Model" which calculates support based on the combined gross income of both parents',
      factors: [
        'Gross monthly income of both parents (pre-tax)',
        'Number of overnight stays per year with each parent (crucial factor)',
        'Cost of health insurance for the child (paid by parent)',
        'Work-related childcare expenses',
        'Extraordinary medical or educational expenses',
        'Other children the parents are legally responsible for'
      ],
      calculation: 'Calculated using the official Colorado Child Support Guidelines worksheet (Worksheet A for sole, Worksheet B for shared physical care)',
      modifications: 'Can be modified if there is a "substantial and continuing change" (usually a 10% change in the support amount)',
      enforcement: 'Enforced through Family Support Registry (FSR) with wage garnishment, tax intercept, and license suspension'
    },
    resources: {
      courts: [
        {
          name: 'Colorado Judicial Branch - Self Help',
          url: 'https://www.courts.state.co.us/Self_Help/family/',
          description: 'Official forms, instructions, and flowcharts for representing yourself'
        },
        {
          name: 'Denver District Court',
          url: 'https://www.courts.state.co.us/Courts/District/Index.cfm?District_ID=2',
          description: 'Resources for the Second Judicial District (Denver County)'
        },
        {
          name: 'Colorado Child Support Services',
          url: 'https://childsupport.state.co.us/',
          description: 'Apply for support services and manage payments'
        }
      ],
      support: [
        {
          name: 'Colorado Legal Services',
          url: 'https://www.coloradolegalservices.org/',
          description: 'Legal aid for low-income Coloradans'
        },
        {
          name: 'Metro Volunteer Lawyers',
          url: 'https://www.denbar.org/mvl',
          description: 'Pro bono legal assistance in the Denver metro area'
        },
        {
          name: 'Violence Free Colorado',
          url: 'https://www.violencefreecolorado.org/',
          description: 'Support for survivors of domestic violence'
        }
      ],
      legal: [
        {
          name: 'Colorado Bar Association - Find a Lawyer',
          url: 'https://www.cobar.org/Find-A-Lawyer',
          description: 'Directory of licensed attorneys in Colorado'
        },
        {
          name: 'Office of the Child\'s Representative',
          url: 'https://coloradochildrep.org/',
          description: 'Information on Guardians ad Litem (GAL) and Child Legal Representatives (CLR)'
        }
      ],
      educational: [
        {
          name: 'Co-Parenting for Resilience',
          url: 'https://disputeresolution.co/parenting-classes',
          description: 'Commonly accepted parenting class for court requirements'
        },
        {
          name: 'Colorado Revised Statutes (Family Law)',
          url: 'https://leg.colorado.gov/agencies/office-legislative-legal-services/colorado-revised-statutes',
          description: 'Access to the official laws (Title 14)'
        }
      ]
    },
    bestInterestsFactors: [
      'The wishes of the child\'s parents as to parenting time',
      'The wishes of the child if he or she is sufficiently mature to express reasoned and independent preferences',
      'The interaction and interrelationship of the child with his or her parents, his or her siblings, and any other person',
      'The child\'s adjustment to his or her home, school, and community',
      'The mental and physical health of all individuals involved',
      'The ability of the parties to encourage the sharing of love, affection, and contact between the child and the other party',
      'Whether the past pattern of involvement of the parties with the child reflects a system of values, time commitment, and mutual support',
      'The physical proximity of the parties to each other as this relates to the practical considerations of parenting time',
      'Whether one of the parties has been a perpetrator of child abuse or neglect',
      'Whether one of the parties has been a perpetrator of domestic violence',
      'The ability of each party to place the needs of the child ahead of his or her own needs'
    ]
  }
};

export default coloradoData;